import axios from "axios";
import { localizedPath } from "../utils/routeHelpers";
import { rememberReturnPath } from "../utils/returnPath";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002/api";
export const FILES_BASE_URL = API_URL.replace(/\/api\/?$/, "");

// Auth przez httpOnly cookies (wzorzec mpracownika) — żadnego JWT w localStorage.
const api = axios.create({
    baseURL: API_URL,
    timeout: 30000,
    withCredentials: true,
});

// Token dla SignalR (cookies nie działają w hubach) — tylko w pamięci modułu.
let _signalRToken = null;
export const setSignalRToken = (token) => {
    _signalRToken = token;
};
export const getSignalRToken = () => _signalRToken;

api.interceptors.request.use((config) => {
    const method = (config.method || "").toLowerCase();
    if (["post", "put", "patch"].includes(method) && !(config.data instanceof FormData)) {
        config.headers["Content-Type"] = "application/json";
    }
    return config;
});

// Trasy dostępne bez logowania — wygasła sesja NIE może z nich wyrzucać
// (osoba zewnętrzna z linkiem do statusu wniosku nie ma i nie potrzebuje konta).
const PUBLIC_PATH_PREFIXES = ["/logowanie", "/login", "/wniosek"];

const redirectToLoginIfNeeded = () => {
    const path = window.location.pathname;
    if (!PUBLIC_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) {
        // To jest TWARDE przeładowanie — stan routera (location.state.from z ProtectedRoute)
        // nie przeżyje, więc cel zapisujemy osobno. Bez tego wejście z linku w kodzie QR przy
        // wygasłej sesji kończyło się na pulpicie, a nie na wyniku weryfikacji przepustki.
        rememberReturnPath(`${path}${window.location.search}${window.location.hash}`);
        window.location.href = localizedPath("login");
    }
};

const clearSession = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("permissions");
    _signalRToken = null;
    // AuthContext nasłuchuje i zeruje stan Reacta — inaczej chroniony UI renderuje martwą sesję.
    window.dispatchEvent(new Event("auth-session-cleared"));
};

// Cichy refresh z kolejką — równoległe 401 czekają na jeden POST /users/refresh.
let isRefreshing = false;
let failedQueue = [];
const processQueue = (error) => {
    failedQueue.forEach((promise) => (error ? promise.reject(error) : promise.resolve()));
    failedQueue = [];
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config || {};
        const status = error.response?.status;

        // Down-detektor: brak odpowiedzi albo 502/503/504 (poza health i anulowanymi).
        const isCanceled = error.code === "ERR_CANCELED" || error.code === "ECONNABORTED";
        const isHealth = (originalRequest.url || "").includes("/health");
        if ((!error.response || [502, 503, 504].includes(status)) && !isCanceled && !isHealth) {
            window.dispatchEvent(new Event("backend-network-error"));
            // Bez tego ekrany logowania pokazują mylące "Nieprawidłowy email lub hasło".
            error.friendlyMessage = error.response
                ? "Serwer chwilowo niedostępny. Spróbuj ponownie za chwilę."
                : "Brak połączenia z serwerem. Spróbuj ponownie za chwilę.";
            return Promise.reject(error);
        }

        // 401 z samego refresh — koniec sesji.
        if (status === 401 && (originalRequest.url || "").includes("/users/refresh")) {
            clearSession();
            redirectToLoginIfNeeded();
            return Promise.reject(error);
        }

        // Zwykły 401 — spróbuj cichego refreshu raz.
        const isAuthEndpoint = (originalRequest.url || "").includes("/users/login");
        if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
            originalRequest._retry = true;
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(() => api(originalRequest));
            }
            isRefreshing = true;
            try {
                const res = await api.post("/users/refresh", {});
                if (res.data?.accessToken) {
                    _signalRToken = res.data.accessToken;
                }
                if (res.data?.permissions) {
                    localStorage.setItem("permissions", JSON.stringify(res.data.permissions));
                }
                processQueue(null);
                return api(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError);
                const refreshStatus = refreshError.response?.status;
                // Wylogowanie TYLKO przy 400/401 z refreshu; 5xx/429/brak sieci = sesja zostaje.
                if (refreshStatus === 400 || refreshStatus === 401) {
                    clearSession();
                    redirectToLoginIfNeeded();
                }
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        error.friendlyMessage =
            error.response?.data?.message || error.response?.data?.error || error.message;
        return Promise.reject(error);
    }
);

export const apiService = {
    // Auth
    login: (credentials) => api.post("/users/login", credentials),
    loginOffice365: (tokenDto) => api.post("/users/login-office365", tokenDto),
    // Potwierdzenie logowania kodem SMS po dłuższej przerwie. Wysyłamy WYŁĄCZNIE challengeId
    // i kod — konto oraz numer telefonu zna serwer (challengeId nie niesie żadnej z tych rzeczy).
    // Dopiero ten endpoint zakłada cookies sesji, więc `login` bez potwierdzenia nie daje dostępu.
    verifyLoginSms: (challengeId, code) => api.post("/users/login/verify-sms", { challengeId, code }),
    resendLoginSms: (challengeId) => api.post("/users/login/resend-sms", { challengeId }),
    getMe: () => api.get("/users/me"),
    refreshToken: () => api.post("/users/refresh", {}),
    logout: () => api.post("/users/logout", {}),
    getO365Config: () => api.get("/users/o365-config"),

    // Użytkownicy
    getUsers: () => api.get("/users"),
    updateUser: (id, data) => api.put(`/users/${id}`, data),

    // Grupy uprawnień (rangi) — zestaw kluczy nadawany całej grupie naraz.
    // GET zwraca RAZEM z listą także katalog uprawnień (sekcje, opisy, poziomy), żeby
    // front nie trzymał własnej kopii słownika — rozjeżdżała się przy każdym nowym kluczu.
    getAccessGroups: () => api.get("/access-groups"),
    createAccessGroup: (data) => api.post("/access-groups", data),
    updateAccessGroup: (id, data) => api.put(`/access-groups/${id}`, data),
    deleteAccessGroup: (id) => api.delete(`/access-groups/${id}`),

    // Whitelista
    getWhitelist: () => api.get("/whitelist"),
    createWhitelistEntry: (data) => api.post("/whitelist", data),
    updateWhitelistEntry: (id, data) => api.put(`/whitelist/${id}`, data),
    deleteWhitelistEntry: (id) => api.delete(`/whitelist/${id}`),

    // Historia logowań
    getLoginHistory: (params) => api.get("/login-history", { params }),

    // Dziennik dostępu do danych wrażliwych (panel administratora).
    getDataAccess: (params) => api.get("/data-access", { params }),
    getDataAccessSummary: (params) => api.get("/data-access/summary", { params }),

    // Konfiguracja O365
    getConfigurations: () => api.get("/configurations"),
    createConfiguration: (data) => api.post("/configurations", data),
    updateConfiguration: (id, data) => api.put(`/configurations/${id}`, data),
    deleteConfiguration: (id) => api.delete(`/configurations/${id}`),

    // Ustawienia runtime aplikacji (tabela AppSettings) — okna weryfikacji, blokada
    // anulowania aktywnej przepustki, karencja PDF. Zapis idzie CAŁYM formularzem naraz:
    // backend waliduje komplet i albo zapisuje wszystko, albo nic.
    getAppSettings: () => api.get("/app-settings"),
    saveAppSettings: (items) => api.put("/app-settings", { items }),

    // Strefy
    getZones: () => api.get("/zones"),
    // SŁOWNIK stref (id, kod, nazwa, „moja strefa") dla ekranów roboczych. Osobny endpoint
    // od /zones, bo tamten zarządza definicjami i stoi za AdminPinGuard — nie-adminowi
    // oddawał 423, więc formularz nie miał z czego wybrać strefy, a wniosku bez strefy
    // nie dało się już zaakceptować.
    getZoneLookup: () => api.get("/zone-lookup"),
    // ── Mapa zakładu ── obraz wgrywany przez administratora; strefy dostają na niej
    // znaczniki (procentowe współrzędne), a kreator wniosku pokazuje ją w oknie modal.
    // Podgląd obrazu (także w adminie) idzie publicznym GET /portal/map — bez tokenu w <img>.
    uploadZonesMap: (file) => {
        const form = new FormData();
        form.append("file", file);
        return api.post("/zones/map", form, { headers: { "Content-Type": "multipart/form-data" } });
    },
    saveZoneMapMarker: (id, data) => api.put(`/zones/${id}/map-marker`, data),
    // Obszar strefy RYSOWANY na mapie (poligon; punkty w procentach 0-100).
    // { points: [{x,y},...] } — min. 3 punkty; { points: null } usuwa obrys.
    saveZoneMapShape: (id, data) => api.put(`/zones/${id}/map-shape`, data),
    // Pracownicy KG możliwi do przypisania do strefy (opiekun / akceptujący) — bez kont portalowych.
    getAssignableZoneUsers: () => api.get("/zones/assignable-users"),
    createZone: (data) => api.post("/zones", data),
    updateZone: (id, data) => api.put(`/zones/${id}`, data),
    deleteZone: (id) => api.delete(`/zones/${id}`),

    // Portal publiczny (bez logowania)
    getPortalAreas: () => api.get("/portal/areas"),
    sendPortalSmsCode: (phone) => api.post("/portal/sms/send", { phone }),
    // Weryfikacja dwukanałowa (sms|email) — używana też przez formularz wewnętrzny,
    // żeby PIN z SMS działał identycznie w obu ścieżkach składania wniosku.
    sendPortalVerificationCode: (channel, target) =>
        api.post("/portal/verification/send", { channel, target }),
    verifyPortalVerificationCode: (channel, target, code) =>
        api.post("/portal/verification/verify", { channel, target, code }),
    verifyPortalSmsCode: (phone, code) => api.post("/portal/sms/verify", { phone, code }),
    // Zdjęcie dokumentu — osobny krok PRZED wysłaniem wniosku: backend zwraca `photoId`,
    // który dopiero trafia do treści wniosku. Dłuższy timeout niż domyślne 30 s, bo plik
    // z telefonu potrafi iść wolno przez sieć komórkową (interceptor sam pomija
    // Content-Type dla FormData, żeby axios ustawił własny boundary).
    uploadPortalPhoto: (file) => {
        const formData = new FormData();
        formData.append("file", file, file?.name || "zdjecie.jpg");
        return api.post("/portal/photo", formData, { timeout: 120000 });
    },
    createPortalRequest: (data) => api.post("/portal/requests", data),
    getPortalRequestStatus: (trackingToken) =>
        api.get(`/portal/requests/${encodeURIComponent(trackingToken)}`),
    // Kod bramowy „ala BLIK" — 4 cyfry ważne 5 minut do ręcznego sprawdzenia przez ochronę,
    // gdy nie da się zeskanować QR. Autoryzacją jest sam trackingToken (jak przy statusie).
    createPortalGateCode: (trackingToken) =>
        api.post(`/portal/requests/${encodeURIComponent(trackingToken)}/gate-code`, {}),
    // PDF przepustki jako BLOB, a nie zwykły link: dokument zawiera dane osobowe, więc
    // nie chcemy go zostawiać w historii przeglądarki jako adres z tokenem w URL-u.
    // Dłuższy timeout — generowanie idzie po stronie serwera.
    downloadPortalRequestPdf: (trackingToken) =>
        api.get(`/portal/requests/${encodeURIComponent(trackingToken)}/pdf`, {
            responseType: "blob",
            timeout: 60000,
        }),
    registerPortalAccount: (data) => api.post("/portal/register", data),

    // Wnioski przepustkowe
    getPasses: (params) => api.get("/passes", { params }),
    // Pulpit: liczniki „na dziś" + 5 ostatnio złożonych — zakres liczy serwer (strefy/ochrona).
    getDashboard: () => api.get("/passes/dashboard"),
    getPass: (id) => api.get(`/passes/${id}`),
    createPass: (data) => api.post("/passes", data),
    submitPass: (id) => api.post(`/passes/${id}/submit`, {}),
    decidePass: (id, data) => api.post(`/passes/${id}/decide`, data),
    // POWÓD ANULOWANIA JEST OBOWIĄZKOWY (min. 5 znaków) — puste ciało dostaje z serwera 400.
    // Powód trafia do wnioskodawcy i do dziennika, więc nie wolno go domyślać się po stronie
    // frontu ("anulowano przez obsługę" nic nikomu nie mówi) — musi go wpisać człowiek.
    cancelPass: (id, data) => api.post(`/passes/${id}/cancel`, data || {}),
    verifyPass: (qrToken) => api.get(`/passes/verify/${encodeURIComponent(qrToken)}`),
    // Przepustki „na teraz" dla ekranu weryfikacji — aktywne + 2 godz. po zakończeniu terminu.
    getActivePassesNow: () => api.get("/passes/active-now"),
    // Dziennik zdarzeń (audyt) — wszystkie śledzone działania użytkowników.
    getAuditEvents: (params) => api.get("/login-history/events", { params }),

    // Powiadomienia w aplikacji (dzwonek) — tylko własne konto.
    getNotifications: () => api.get("/notifications"),
    markNotificationRead: (id) => api.post(`/notifications/${id}/read`, {}),
    markAllNotificationsRead: () => api.post("/notifications/read-all", {}),

    // Treści wiadomości (SMS/e-mail) edytowane w panelu administratora.
    getMessageTemplates: () => api.get("/message-templates"),
    saveMessageTemplate: (key, data) => api.put(`/message-templates/${encodeURIComponent(key)}`, data),
    resetMessageTemplate: (key) => api.post(`/message-templates/${encodeURIComponent(key)}/reset`, {}),
    // Kod bramowy idzie POST-em w ciele żądania — jednorazowe poświadczenie wejścia nie może
    // wylądować w logach nginx ani w historii przeglądarki, a tam trafiają adresy.
    verifyGateCode: (code) => api.post("/passes/verify-gate-code", { code }),
    // UNIWERSALNA WYSZUKIWARKA WERYFIKACJI — jedno pole na numer wniosku (BP-2026-000004),
    // token z kodu QR i 4-cyfrowy kod z telefonu gościa; format rozpoznaje serwer.
    // Tak samo POST-em jak verifyGateCode i z tego samego powodu: zapytanie może NIEŚĆ kod
    // bramowy, czyli jednorazowe poświadczenie wejścia na teren zakładu.
    // Zakres zwróconych danych liczy serwer wg uprawnień pytającego — cudza przepustka
    // wygląda jak nieistniejąca (numery są sekwencyjne, więc inaczej dałoby się przeskanować
    // rejestr numer po numerze).
    // `reason` (powód kontroli) jest OPCJONALNY — trafia do dziennika sprawdzeń, nie do gościa.
    verifyLookup: (query, reason) => api.post("/passes/verify-lookup", { query, reason: reason || null }),

    // Karty
    getCards: (params) => api.get("/cards", { params }),
    getCardEvents: (id) => api.get(`/cards/${id}/events`),
    createCard: (data) => api.post("/cards", data),
    blockCard: (id, data) => api.post(`/cards/${id}/block`, data || {}),
    returnCard: (id, data) => api.post(`/cards/${id}/return`, data || {}),

    // Zdrowie
    getHealth: () => api.get("/health"),

    // ── Rozliczenie przepustki i opiekun klienta (host) ──
    // Rozliczenie jest obowiązkowe po zakończeniu wizyty: notatka jest wymagana także po
    // przycięciu białych znaków (backend odrzuca sam string ze spacji).
    settlePass: (id, data) => api.post(`/passes/${id}/settle`, data),
    // Rozliczenie ZBIORCZE — serwer ocenia każdą przepustkę osobno i oddaje wykaz
    // „rozliczone / pominięte z powodem", więc odpowiedź trzeba pokazać w całości.
    settlePassesBulk: (data) => api.post("/passes/settle-bulk", data),
    // Skan podpisanej zgody RODO dołączany PRZY ROZLICZENIU (opiekun nie ma dostępu do kartoteki).
    uploadPassConsentScan: (id, file) => {
        const form = new FormData();
        form.append("file", file);
        return api.post(`/passes/${id}/privacy-consent/scan`, form, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
    // Podgląd skanu zgody dołączonego DO WNIOSKU (gość bez kartoteki — jedyny przypadek,
    // w którym skan nie ma magazynu nadrzędnego). Zwraca PLIK, więc adres otwieramy wprost;
    // bramka jest ta sama co przy dołączaniu (opiekun wizyty albo rozliczający), a odczyt
    // zostawia ślad w dzienniku dostępu.
    passConsentScanUrl: (id) => `${API_URL}/passes/${id}/privacy-consent/scan`,
    // PUSTE ciało ZDEJMUJE opiekuna — dlatego domyślnie wysyłamy {}, a nie null.
    assignPassHost: (id, data) => api.post(`/passes/${id}/host`, data || {}),
    // WAGA POJAZDU — ochrona (weryfikacja) lub obsługa rejestru wpisuje wagę wjazdu i/lub wyjazdu.
    // Oba pola są NIEZALEŻNE i OPCJONALNE (co najmniej jedno wymagane): { entryKg?, exitKg? } w kg.
    // Zwraca zaktualizowany wniosek (kształt elementu listy) z polami wagi i znacznikami czasu.
    setPassVehicleWeight: (id, data) => api.post(`/passes/${id}/vehicle-weight`, data),
    // Ruch OSOBOWY na bramie. `direction` ("entry"/"exit") zapisuje biezaca godzine —
    // ochrona ma nacisnac jeden przycisk, a nie przepisywac zegarek.
    setPassMovement: (id, data) => api.post(`/passes/${id}/movement`, data),

    // Przepustki pracownicze — lista kandydatow i masowe wystawianie.
    // Wystawienie przepustek DLA KARTOTEK (1..N kont naraz) — od razu ZATWIERDZONE
    // (bez akceptacji; decyzja klienta 2026-08-08), osoby dostają SMS/e-mail z kodem QR
    // jak przy zatwierdzeniu własnego wniosku.
    issuePassesForAccounts: (data) => api.post("/passes/issue-for", data),
    getEmployeeCandidates: (params) => api.get("/employee-passes/candidates", { params }),
    issueEmployeePasses: (data) => api.post("/employee-passes/issue", data),

    // Kartoteka zakladana przy okienku + papierowa zgoda RODO.
    // Panel administratora: PIN. O dostepie rozstrzyga serwer (kod 423 na modulach),
    // te wywolania obsluguja tylko ekran odblokowania i nadawanie PIN-u przez admina.
    getAdminAccessStatus: () => api.get("/admin-access/status"),
    unlockAdminAccess: (pin) => api.post("/admin-access/unlock", { pin }),
    lockAdminAccess: () => api.post("/admin-access/lock"),
    setUserAdminPin: (userId, pin) => api.put(`/admin-access/pin/${userId}`, { pin }),

    createPortalAccount: (data) => api.post("/portal-accounts", data),
    confirmAccountPrivacyConsent: (id) => api.post(`/portal-accounts/${id}/privacy-consent`),
    resendAccountInvitation: (id) => api.post(`/portal-accounts/${id}/invitation`),
    uploadAccountConsentScan: (id, file) => {
        const form = new FormData();
        form.append("file", file);
        return api.post(`/portal-accounts/${id}/privacy-consent/scan`, form);
    },

    // Raport z tabeli — plik sklada SERWER (czcionki z polskimi znakami, logo, uklad),
    // front przysyla to, co widac na ekranie po sortowaniu i filtrach.
    exportTable: (format, payload) =>
        api.post(`/exports/table?format=${encodeURIComponent(format)}`, payload, { responseType: "blob" }),

    // ── Dokumenty przepustki (pliki binarne) ──
    // responseType "blob" jest konieczny — bez niego axios psuje bajty PDF-a/JPEG-a, próbując
    // zdekodować je jako tekst. Błędy z tych wywołań mają ciało typu Blob, więc komunikat
    // czytamy przez readBlobErrorMessage (interceptor nie potrafi z niego wyciągnąć `message`).
    getPassPhoto: (id) => api.get(`/passes/${id}/photo`, { responseType: "blob" }),
    downloadPassPdf: (id) => api.get(`/passes/${id}/pdf`, { responseType: "blob", timeout: 60000 }),
    downloadPassPrivacyPdf: (id) =>
        api.get(`/passes/${id}/rodo-pdf`, { responseType: "blob", timeout: 60000 }),

    // ── Konta przepustek (osoby ZEWNĘTRZNE z kontem portalu) ──
    // Osobne od /users: tam są pracownicy KG, tu goście zakładający konto przez portal.
    getPortalAccounts: (params) => api.get("/portal-accounts", { params }),

    // ── Kategorie wizytatorow ("student", "pracownicy wlasni", "firma X") ──
    // Filtr `categoryId` dziala PO STRONIE SERWERA, przed obcieciem listy do 500 pozycji —
    // odsianie w przegladarce dawaloby "studentow sposrod 500 najswiezszych kartotek".
    getVisitorCategories: () => api.get("/portal-accounts/categories"),
    createVisitorCategory: (data) => api.post("/portal-accounts/categories", data),
    updateVisitorCategory: (id, data) => api.put(`/portal-accounts/categories/${id}`, data),
    deleteVisitorCategory: (id) => api.delete(`/portal-accounts/categories/${id}`),
    setAccountCategories: (accountId, categoryIds) =>
        api.put(`/portal-accounts/${accountId}/categories`, { categoryIds }),
    // Odsłonięcie numeru dokumentu i danych urodzenia jest zapisywane w dzienniku po stronie serwera — wołać
    // wyłącznie na świadomą akcję użytkownika, nigdy „na wszelki wypadek" przy renderze listy.
    revealPortalAccount: (id) => api.get(`/portal-accounts/${id}/reveal`),
    blockPortalAccount: (id, data) => api.post(`/portal-accounts/${id}/block`, data),
    unblockPortalAccount: (id) => api.post(`/portal-accounts/${id}/unblock`, {}),
    getPortalAccountRequests: (id) => api.get(`/portal-accounts/${id}/requests`),
    // Karta konta: profil, liczby wniosków, stan blokady, ostatnie logowanie oraz informacja,
    // co wolno zrobić TEMU operatorowi (`editable`). Dane wrażliwe wracają wyłącznie zamaskowane.
    getPortalAccount: (id) => api.get(`/portal-accounts/${id}`),
    // Korekta danych osobowych. SEMANTYKA PÓL: pole nieobecne = nie zmieniaj, pusty ciąg = wyczyść —
    // wysyłanie całego formularza skasowałoby dane, których nikt nie dotknął (numer dokumentu jest
    // zamaskowany, więc jego pole w formularzu startuje puste). Wysyłaj TYLKO faktycznie zmienione pola.
    updatePortalAccount: (id, data) => api.put(`/portal-accounts/${id}`, data),
    getPortalAccountDocuments: (id) => api.get(`/portal-accounts/${id}/documents`),
    getPortalAccountActivity: (id) => api.get(`/portal-accounts/${id}/activity`),
    // Pobranie pliku wniosku po adresie `apiPath` z karty konta (`/passes/{id}/pdf`, `/rodo-pdf`,
    // `/photo`). Instancja axiosa ma `withCredentials`, więc ciasteczko sesji idzie razem z żądaniem —
    // te endpointy wymagają zalogowania i same zapisują pobranie w dzienniku. Ścieżka MUSI być
    // względna (zaczynać się od „/") — adres absolutny wysłałby ciasteczka poza nasz serwis.
    downloadPortalAccountDocument: (apiPath) =>
        api.get(apiPath, { responseType: "blob", timeout: 60000 }),

    // ── Zakładanie konta: potwierdzenie adresu e-mail kodem ──
    // Adresu NIE wysyłamy z przeglądarki — backend bierze go z wniosku wskazanego przez
    // setupToken. Gdyby front mógł podać adres, link rejestracyjny pozwalałby przekierować
    // kod na cudzą skrzynkę. Samo założenie konta idzie dalej przez `registerPortalAccount`,
    // które od teraz przyjmuje `{ setupToken, code, password }`.
    sendPortalRegistrationCode: (setupToken) =>
        api.post("/portal/register/send-code", { setupToken }),

    // ── Profil osoby zewnętrznej („moje konto") ──
    // Wymaga zalogowania. Na trasach portalu (`/wniosek/...`) wygasła sesja NIE przekierowuje
    // na logowanie (PUBLIC_PATH_PREFIXES), więc 401 wraca do strony i to ona pokazuje
    // zaproszenie do zalogowania — gość bez konta nie może zostać wyrzucony z portalu.
    getPortalMe: () => api.get("/portal/me"),
    getPortalMyRequests: () => api.get("/portal/me/requests"),
    // Zgody i oświadczenia WŁAŚCICIELA konta: najnowszy własny wniosek ze znacznikami
    // akceptacji (RODO + regulamin) — panel „Moje dane" pokazuje daty i daje PDF
    // potwierdzenia (endpoint /passes/{id}/rodo-pdf wpuszcza właściciela wniosku).
    getPortalMyConsents: () => api.get("/portal/me/consents"),
    // Zdjęcie dokumentu WŁAŚCICIELA konta (panel „Moje dane") — blob, bo plik siedzi za
    // autoryzacją cookie: zwykły <img src> nie poniósłby sesji przy osobnej domenie API.
    // 404 = brak zdjęcia w wnioskach (panel pokazuje wtedy miejsce „brak zdjęcia").
    getPortalMyPhoto: () => api.get("/portal/me/photo", { responseType: "blob" }),
    // Pełny numer dokumentu i data urodzenia. KAŻDE wywołanie jest zapisywane w dzienniku po stronie
    // serwera (kto, kiedy, z jakiego IP) — wołać wyłącznie na świadome kliknięcie „oczka",
    // nigdy przy renderze profilu.
    // Odslanianie WLASNYCH danych: najpierw kod na e-mail KONTA, potem kod w POST.
    sendPortalRevealCode: () => api.post("/portal/me/profile/reveal-code"),
    revealPortalMyProfile: (code) => api.post("/portal/me/profile/reveal", { code }),
    // Weryfikacja e-mail / telefonu PO fakcie (z zakładki „Moje dane") + zmiana hasła.
    sendPortalMyEmailCode: () => api.post("/portal/me/email/send-code", {}),
    verifyPortalMyEmail: (code) => api.post("/portal/me/email/verify", { code }),
    sendPortalMyPhoneCode: () => api.post("/portal/me/phone/send-code", {}),
    verifyPortalMyPhone: (code) => api.post("/portal/me/phone/verify", { code }),
    changePortalMyPassword: (currentPassword, newPassword) =>
        api.post("/portal/me/password", { currentPassword, newPassword }),
    // Reset zapomnianego hasła (anonimowo) — prośba i potwierdzenie z linku.
    requestPortalPasswordReset: (email) => api.post("/portal/password/reset/request", { email }),
    // Zaproszenie PRZEDSTAWICIELA FIRMY (Type 5) konsumuje osobny endpoint — token leży w innej
    // tabeli niż reset kartoteki, a link w obu przypadkach prowadzi na /reset-hasla/{token}.
    confirmCompanyInvitation: (token, newPassword) =>
        api.post("/users/company-invitation/confirm", { token, newPassword }),
    resendCompanyInvitation: (companyId, userId) =>
        api.post(`/companies/${companyId}/accounts/${userId}/invitation`),

    confirmPortalPasswordReset: (token, newPassword) =>
        api.post("/portal/password/reset/confirm", { token, newPassword }),

    // ── Logowanie dwuskładnikowe (MFA) — konto ZALOGOWANE zarządza własnym drugim składnikiem ──
    // Kod potwierdzający numer idzie SMS-em; po potwierdzeniu przeglądarka jest zapamiętywana
    // ciasteczkiem httpOnly, więc kolejne logowania z niej nie proszą o kod.
    getMfaStatus: () => api.get("/users/mfa"),
    // Haslo jest wymagane takze przy WLACZANIU: ten krok decyduje, na czyj telefon pojdzie
    // drugi skladnik, wiec sama sesja nie wystarcza (przejety ekran przekierowalby MFA).
    startMfaPhone: (phone, password) => api.post("/users/mfa/phone/start", { phone, password }),
    confirmMfaPhone: (code) => api.post("/users/mfa/phone/confirm", { code }),
    disableMfa: (password) => api.post("/users/mfa/disable", { password }),
    revokeTrustedDevice: (id) => api.post(`/users/mfa/devices/${id}/revoke`),
    // Administracyjne zdjecie MFA z cudzego konta (PERM:UsersManage) — droga wyjscia, gdy
    // wlasciciel stracil dostep do potwierdzonego numeru.
    resetUserMfa: (userId) => api.post(`/users/${userId}/mfa/reset`),

    // ── Firmy zewnętrzne (panel administratora, PERM:CompaniesManage + PIN) ──
    // Lista wraca z licznikami (accountsCount, employeesCount, activePassesCount) — front
    // ich nie dolicza, bo kartoteki i przepustki zna tylko serwer.
    getCompanies: () => api.get("/companies"),
    createCompany: (data) => api.post("/companies", data),
    updateCompany: (id, data) => api.put(`/companies/${id}`, data),
    // Serwer odmawia usunięcia firmy z kontami lub kartotekami (jak przy grupach uprawnień) —
    // front pokazuje jego komunikat, niczego nie rozstrzyga sam.
    deleteCompany: (id) => api.delete(`/companies/${id}`),
    getCompanyAccounts: (id) => api.get(`/companies/${id}/accounts`),
    // Konto przedstawiciela (User Type=5): { firstName, lastName, email }. Zaproszenie
    // „ustaw hasło" wysyła serwer e-mailem — front po sukcesie tylko o tym informuje.
    createCompanyAccount: (id, data) => api.post(`/companies/${id}/accounts`, data),
    // { userId } przypina ISTNIEJĄCEGO użytkownika do firmy (typu nie zmienia — pracownik KG
    // może być przedstawicielem), { userId, detach: true } odpina.
    mapCompanyUser: (id, data) => api.post(`/companies/${id}/map-user`, data),
    // Wyszukiwarka kont do mapowania — maks 20 wyników, bez danych wrażliwych.
    // purpose="owner" zawęża wynik do pracowników KG PO STRONIE SERWERA. Filtrowanie rang
    // dopiero we froncie gubiło kandydatów: serwer tnie do 20 wierszy, więc przy wielu kontach
    // firmowych właściwy pracownik nie mieścił się w odpowiedzi i opiekuna nie dało się wskazać.
    searchCompanyUsers: (q, purpose) =>
        api.get("/companies/user-search", { params: { q, purpose } }),
    // Kartoteki pracowników firmy z perspektywy ADMINA — numer dokumentu wraca ZAMASKOWANY
    // (pełne numery nie schodzą na listę), stąd brak osobnego wpisu Document w dzienniku.
    // KSZTAŁT ODPOWIEDZI: tablica, gdy widać wszystkich; obiekt
    // { items, undisclosedCount, undisclosedNote }, gdy część kartotek firmy nie ma jeszcze
    // ŻADNEGO wniosku — takich osób serwer nie wydaje (patrz filtr kartotek nieujawnionych),
    // oddaje tylko ich liczbę. Odbiorca musi obsłużyć oba kształty.
    getCompanyEmployees: (id) => api.get(`/companies/${id}/employees`),
    // Oś czasu operacji firmy (wpisy dziennika + wnioski) — `params` np. { limit }.
    // Odpowiedź: { items: [...], recentRequests: [...] }; rozróżnienie wierszy po `kind`.
    getCompanyActivity: (id, params) => api.get(`/companies/${id}/activity`, { params }),

    // ── Kartoteki firm dla PRACOWNIKA KG (/firmy, PERM:CompaniesView, BEZ PIN-u) ──
    // Osobny kontroler od /companies celowo: tamten zakłada i zmienia (panel administratora),
    // ten TYLKO czyta. Dwa endpointy zamiast jednego, bo lista ma być lekka — szczegóły
    // (pracownicy + ostatnie wnioski) ściągamy dopiero po kliknięciu w kartę firmy.
    // Lista: id, name, taxId, city, isActive, ownerUserId, ownerName, employeeCount,
    // activePassCount, pendingRequestCount — liczniki wylicza serwer.
    getCompanyDirectory: () => api.get("/company-directory"),
    // Szczegóły: dane firmy + owner + employees (dokument ZAMASKOWANY) + recentRequests.
    // Każdy odczyt zostawia ślad w dzienniku dostępu po stronie serwera — wołać na
    // świadome otwarcie karty firmy, nigdy w pętli po całej liście.
    getCompanyDirectoryDetails: (id) => api.get(`/company-directory/${id}`),

    // ── Panel firmowy (/firma) ── bramką jest claim companyId w tokenie, nie klucz PERM,
    // dlatego te wywołania nie mają odpowiednika w katalogu uprawnień.
    getCompanyPanelMe: () => api.get("/company-panel/me"),
    // Kartoteki firmy — dokument wraca ZAMASKOWANY; każdy odczyt trafia do dziennika
    // dostępu po stronie serwera (ActorKind=Company).
    getCompanyPanelEmployees: () => api.get("/company-panel/employees"),
    // Nowa kartoteka pracownika firmy — `attest: true` jest OBOWIĄZKOWE (firma ręczy
    // za autentyczność danych; bez oświadczenia serwer odrzuca).
    createCompanyEmployee: (data) => api.post("/company-panel/employees", data),
    // Import partii { rows: [...], attest: true } — maks 200 wierszy; wynik per wiersz
    // {row, ok, error, accountId}, więc błąd jednego wiersza nie wywraca reszty.
    importCompanyEmployees: (data) => api.post("/company-panel/employees/import", data),
    // Blokada kartoteki pracownika PRZEZ FIRMĘ: { blocked: true, reason } albo { blocked: false }.
    // To ta sama blokada co po stronie Biura — unieważnia też przepustki już wydane, a osobie
    // będącej na terenie zostawia samo WYJŚCIE. Odblokowanie NIE wskrzesza przepustek.
    blockCompanyEmployee: (userId, data) =>
        api.post(`/company-panel/employees/${userId}/block`, data),
    // Usunięcie kartoteki — serwer przyjmie je WYŁĄCZNIE dla osoby bez żadnego wniosku;
    // przy historii oddaje 409 z podpowiedzią, żeby zamiast tego zablokować.
    deleteCompanyEmployee: (userId) => api.delete(`/company-panel/employees/${userId}`),
    getCompanyPanelRequests: () => api.get("/company-panel/requests"),
    // Wniosek w imieniu kartoteki firmy: { accountUserId, kind, periods, zoneIds, purpose,
    // vehicle?, attest: true } — telefon/e-mail pracownika idzie bez weryfikacji SMS (firma ręczy).
    createCompanyPanelRequest: (data) => api.post("/company-panel/requests", data),
    // Odnowienie tworzy NOWY wniosek (kopia osoby/stref/celu) z terminami { periods: [...] } —
    // stary zostaje nietknięty, odpowiedź niesie numer nowego.
    renewCompanyPanelRequest: (id, data) => api.post(`/company-panel/requests/${id}/renew`, data),
};

/**
 * Komunikat błędu z odpowiedzi pobieranej jako Blob (PDF, zdjęcie dokumentu).
 * Dla `responseType: "blob"` ciało błędu 403/404 też jest Blobem, więc interceptor nie
 * odczyta z niego `message` i użytkownik zobaczyłby „Request failed with status code 403".
 */
export const readBlobErrorMessage = async (error, fallback) => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            const text = await data.text();
            const parsed = JSON.parse(text);
            if (parsed?.message) return parsed.message;
        } catch {
            // odpowiedź nie była JSON-em (np. HTML z proxy) — zostaje komunikat zapasowy
        }
    }
    return error?.friendlyMessage || fallback;
};

/** Nazwa pliku z nagłówka Content-Disposition; `fallback`, gdy nagłówka brak. */
export const fileNameFromResponse = (response, fallback) => {
    const header = response?.headers?.["content-disposition"] || "";
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    if (!match) return fallback;
    try {
        return decodeURIComponent(match[1]);
    } catch {
        return match[1];
    }
};

export default apiService;
